import { useGetPessoa } from "data/api/gestao/pessoa";
import { PessoasModel } from "model/api/gestao/pessoa/pessoas-model";
import { EnumTipoPessoas } from "model/enums/enum-tipo-pessoas";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { CardNaoEncontrado } from "views/components/cards";
import { InformacaoIcon, SalvarIcon, VoltarIcon } from "views/components/icons";
import { useStyles } from "./cadastro-resposta-styles";
import { Box, Button, Checkbox, Grid, IconButton, Typography } from "@material-ui/core";
import { CardRevendaRespostaPropaganda } from "views/components/cards/card-revenda-resposta-propaganda/card-revenda-resposta-propaganda";
import { CardClienteRespostaPropaganda } from "views/components/cards/card-cliente-resposta-propaganda/card-cliente-resposta-propaganda";
import { guidEmpty } from "utils/guid-empty";
import { useCadastros, useSessaoAtual, useToastSaurus } from "services/app";
import { CircularLoading } from "views/components/utils";
import { TextFieldSaurus } from "views/components/controles/inputs";
import { usePutDestinatarioVarios } from "data/api/gestao/propaganda/put-destinatarios-varios";
import { DestinatarioPostModel } from "model/api/gestao/destinatarios/destinatario-post-model";
import { Paginacao } from "views/components/paginacao";
import { PesquisaCliente } from "./components/pesquisa-cliente/pesquisa-cliente";

export interface PropagandaRespostaGridProps {
    propagandaId: string,
    revendaNome: string
}

export const CadastroResposta = (props: PropagandaRespostaGridProps) => {
    const { tipoUsuario } = useSessaoAtual();
    const { showToast } = useToastSaurus();
    const { getPessoa, carregando: carregandoPessoa } = useGetPessoa();
    const { putDestinatarioVarios, carregando: carregandoDestinatariosVarios } = usePutDestinatarioVarios();
    const classes = useStyles();
    const { abrirCadastroPropagandaAddParametros } = useCadastros()

    const isTipoRevenda = tipoUsuario() === EnumTipoPessoas.Revendedor;

    const [currentStep, setCurrentStep] = useState<1 | 2>(() => isTipoRevenda ? 2 : 1); // NOVO: Estado para controlar a etapa 
    const [revendasState, setRevendasState] = useState<Array<PessoasModel>>();
    const [clientesState, setClientesState] = useState({
        page: 1,
        totalPages: 0,
        totalResults: 0,
        list: Array<PessoasModel>(),
    });
    const [revendaSelecionada, setRevendaSelecionada] = useState<PessoasModel | null>(null);
    const [clientesSelecionadoState, setClientesSelecionadoState] = useState<Array<PessoasModel>>([]);
    const isCarregando = [carregandoPessoa, carregandoDestinatariosVarios].includes(true);

    // STATES PARA PESQUISA
    const [searchRevendaState, setSearchRevendaState] = useState<string>('');
    const [searchClienteState, setSearchClienteState] = useState<string>('');
    // const [gerenteState, setGerenteState] = useState<PessoaModel | null>(null);

    const revendaNome = revendaSelecionada?.nomeComercial || props.revendaNome

    const handleNovosDestinatarios = async (parametros: string) => {
        const clientes = clientesSelecionadoState
        const novosDestinatarios = clientes.map(cliente => {
            const novoDestinatario: DestinatarioPostModel = {
                id: guidEmpty(),
                clienteId: cliente.id,
                referencia: cliente.cpfCnpj,
                nivel: 0,
                ativo: true,
                parametros: parametros,
                propagandaId: props.propagandaId,
            }
            return novoDestinatario
        })
        const res = await putDestinatarioVarios(novosDestinatarios);
        if (res.erro) throw res.erro;

        showToast('success', 'Destinatários adicionados com sucesso.');
        setClientesSelecionadoState([]);
        if (!isTipoRevenda) {
            setCurrentStep(1);
        }
        setRevendaSelecionada(null);
        setSearchClienteState('');
        setSearchRevendaState('');
    }

    const onClickEnviarDestinatarios = () => {
        abrirCadastroPropagandaAddParametros(handleNovosDestinatarios)
    }

    // const handleGerenteChange = (retorno: retornoAutoComplete) => {
    //     setGerenteState(retorno?.value ?? null)
    // }

    const getClientesWrapper = useCallback(
        async (page: number, revendaNome?: string, searchCliente?: string) => {
            if (!revendaNome) {
                setClientesState({
                    page: 1,
                    totalPages: 0,
                    totalResults: 0,
                    list: Array<PessoasModel>(),
                });
                return;
            }
            try {
                const query = `tipoPessoa=${EnumTipoPessoas.Cliente}&pageSize=1000&revenda=${revendaNome}${searchCliente ? `&query=${searchCliente}` : ''}&status=0`
                const res = await getPessoa(page, query);

                if (res.erro) throw res.erro;
                const clientes = res.resultado?.data.list;

                setClientesState({
                    list: clientes || [],
                    totalPages: res.resultado?.data.totalPages || 0,
                    totalResults: res.resultado?.data.totalResults || 0,
                    page: res.resultado?.data.pageIndex || 1,
                });

            } catch (e: any) {
                showToast('error', e.message)
            }
        },
        [getPessoa, showToast],
    )

    const getRevendasWrapper = useCallback(
        async (value?: string) => {
            try {
                let query = `tipoPessoa=${EnumTipoPessoas.Revendedor}&pageSize=0`;

                if (value) {
                    query += `&query=${value}`;
                }

                // if (gerenteState) {
                //     query += `&gerenteId=${gerenteState.id}`;
                // }

                const res = await getPessoa(1, query);

                if (res.erro) throw res.erro;

                const todasAsRevendas = res.resultado?.data.list;

                if (todasAsRevendas) {
                    const revendasAtivas = todasAsRevendas.filter(revenda => revenda.status === 0);

                    setRevendasState(revendasAtivas);
                } else {
                    setRevendasState([]);
                }

            } catch (e: any) {
                showToast('error', e.message);
            }
        },
        [getPessoa, showToast],
    );

    const cardSelecionado = (model: PessoasModel) => {
        setClientesSelecionadoState(prev => {
            const anteriores = prev
            if (anteriores.some(cliente => cliente.id === model.id)) {
                return [...prev.filter(cliente => cliente.id !== model.id)]
            }
            return [...anteriores, model]
        })
    }

    useEffect(() => {
        if (currentStep === 1) {
            getRevendasWrapper('');
        }
    }, [currentStep, getRevendasWrapper]);

    useEffect(() => {
        if (currentStep === 2) {
            getClientesWrapper(1, revendaNome, searchClienteState);
        }
    }, [currentStep, getClientesWrapper, revendaNome, searchClienteState])

    const debounce = useCallback((func: (...args: any[]) => void, delay: number) => {
        let timeoutId: NodeJS.Timeout
        return (...args: any[]) => {
            clearTimeout(timeoutId)
            timeoutId = setTimeout(() => func(...args), delay)
        }
    }, [])

    const debounceRevenda = useMemo(() => debounce((value) => {
        if (!value) return;
        getRevendasWrapper(value)
    }, 400), [debounce, getRevendasWrapper])

    useEffect(() => {
        if (currentStep === 1)
            debounceRevenda(searchRevendaState)
    }, [debounceRevenda, searchRevendaState, currentStep])

    const onRevendaSelected = (revenda: PessoasModel) => {
        setRevendaSelecionada(revenda);
        setClientesSelecionadoState([]);
        setSearchClienteState('');
        setCurrentStep(2);
    };

    const handleGoBack = () => {
        setClientesState({
            page: 1,
            totalPages: 0,
            totalResults: 0,
            list: [],
        });
        setCurrentStep(1);
    }

    const pageChanged = useCallback(
        async (newPage: number) => {
            if (newPage <= clientesState.totalPages || newPage > 0) {
                await getClientesWrapper(newPage, revendaNome, searchClienteState);
            }
        },
        [getClientesWrapper, revendaNome, searchClienteState, clientesState.totalPages]
    );

    const renderStep1 = () => (
        <Fragment>
            {/* TODO: voltar para o filtro de gerente após a atualização da api */}
            {/* <Grid item xs={12} md={6}>
                <AutocompleteGerentes
                    size="medium"
                    label="Filtrar Gerente"
                    onChange={handleGerenteChange}
                    fetchOnLoad
                />
            </Grid> */}
            <Grid item xs={12} md={6}>
                <TextFieldSaurus
                    searchable
                    value={searchRevendaState}
                    label='Pesquisar Revenda'
                    onChange={(e: any) => setSearchRevendaState(e.target.value)}
                />
            </Grid>
            <Grid item xs={12} className={classes.container}>
                {revendasState?.length === 0 && !isCarregando && (
                    <CardNaoEncontrado
                        mensagem={`Nenhuma Revenda Encontrada.`}
                        icon={<InformacaoIcon tipo="GERAL" />}
                    />
                )}
                {revendasState?.map((revenda) => (
                    <CardRevendaRespostaPropaganda
                        selected={revenda.id === revendaSelecionada?.id}
                        onCheck={() => { }}
                        onClick={onRevendaSelected}
                        model={revenda}
                        key={revenda.id}
                    />
                ))}
            </Grid>
        </Fragment>
    );

    const selectAll = useCallback(() => {
        if (!clientesState?.list?.length) return;
        const listIds = new Set((clientesState.list || []).map(x => x.id));
        setClientesSelecionadoState(prev => {
            const selectedInList = prev.filter(x => listIds.has(x.id)).length;
            if (selectedInList === clientesState.list.length) {
                return prev.filter(x => !listIds.has(x.id));
            }
            const map = new Map(prev.map(x => [x.id, x]));
            for (const cliente of clientesState.list) {
                if (!map.has(cliente.id)) map.set(cliente.id, cliente);
            }
            return Array.from(map.values());
        });
    }, [clientesState?.list])

    const renderStep2 = () => (
        <div className={classes.rootContainer}>
            {/* Header da Etapa 2 com botão de Voltar */}
            <Grid container item xs={12} alignItems="center" spacing={2} style={{ padding: '8px' }}>
                {isTipoRevenda ? '' : (
                    <Grid item>
                        <IconButton onClick={handleGoBack} size="small">
                            <VoltarIcon tipo="BUTTON" />
                        </IconButton>
                    </Grid>
                )}

                <Grid item>
                    <Typography variant="h6">
                        <b>{revendaSelecionada?.nomeComercial}</b>
                    </Typography>
                    <Typography variant="caption">Selecione os clientes</Typography>
                </Grid>
            </Grid>

            {/* Filtro de Cliente */}
            <PesquisaCliente setPesquisa={setSearchClienteState} />

            <Paginacao
                pageChanged={pageChanged}
                totalPages={clientesState.totalPages}
                totalRegisters={clientesState.totalResults}
                currentPage={clientesState.page}
            />

            <Box display="flex" width="100%" justifyContent="flex-end" mb={2}>
                <Button variant="outlined" onClick={selectAll}>
                    <Checkbox
                        checked={clientesState?.list.filter(x => clientesSelecionadoState.some(y => y.id === x.id)).length === clientesState?.list?.length}
                    />
                    Selecionar Todos
                </Button>
            </Box>

            {/* Área de Conteúdo com Scroll */}
            <div className={classes.contentArea}>
                <Grid container>
                    <Grid item xs={12}>
                        {(clientesState?.list?.length ?? 0) === 0 && !isCarregando && (
                            <CardNaoEncontrado
                                mensagem={`Nenhum Cliente Encontrado.`}
                                icon={<InformacaoIcon tipo="GERAL" />}
                            />
                        )}
                        {clientesState?.list?.map((cliente, index) => (
                            <CardClienteRespostaPropaganda
                                selected={clientesSelecionadoState.some(x => x.id === cliente.id)}
                                onCheck={() => { }}
                                onClick={cardSelecionado}
                                model={cliente}
                                key={cliente.id}
                            />
                        ))}
                    </Grid>
                </Grid>
            </div>

            {/* Rodapé Fixo com Botão Salvar */}
            <div className={classes.footerArea}>
                <Button
                    fullWidth
                    variant='contained'
                    color='primary'
                    onClick={onClickEnviarDestinatarios}
                    disabled={clientesSelecionadoState.length === 0 || carregandoDestinatariosVarios}
                    size='large'
                >
                    <SalvarIcon tipo='BUTTON_PRIMARY' />
                    Adicionar Clientes Selecionados ({clientesSelecionadoState.length})
                </Button>
            </div>
        </div>
    );


    return (
        <Grid container spacing={2} className={classes.containerCadastro}>
            {isCarregando && <CircularLoading tipo="FULLSIZED" />}
            {currentStep === 1 ? renderStep1() : renderStep2()}
        </Grid>
    );
}

